import "./export-controller-custom-filename.css";
import { Loader, LoaderTypes, TextInput } from "~/core";
import { messages } from "~/action-panel/components/rec-module/components/i18n-messages";
import React, { useEffect, useState } from "react";
import {
    REC_TYPE_NAME_EQUATION_APPLICATION,
    REC_TYPE_NAME_MANUAL_APPLICATION,
    RecSummary,
} from "~/recs-events/recs/model";
import { FileImportAPI, MessageDescriptor, RecAPI } from "@ai360/core";
import { getTheUserGuid } from "~/login";
import { groupBy, reduce, uniq, uniqBy } from "lodash";
import { ProductMix } from "~/action-panel/components/common/product-blend-modal/model";
import { useIntl } from "react-intl";
import { useAppSelector } from "~/store/hooks";

interface IRecFilenames {
    customerName: string;
    farmName: string;
    fieldName: string;
    customerGuid: string;
    files: INutrientFilename[];
}

interface IRecProduct {
    recGeneralGuid: string | null;
    nutrient: string | null;
    productMixName: string | null;
}

interface INutrientFilename extends IRecProduct {
    filename: string | null;
}

export interface IExportControllerFilenameOutput {
    zipFilename: string;
    exportFilenames: FileImportAPI.ExportRecFilename[];
}

export enum ExportGrouping {
    Field = "Field",
    Farm = "Farm",
    Grower = "Grower",
    AllGrowers = "AllGrowers",
}

export const exportGroupingOptions = [
    { label: "Field", value: ExportGrouping.Field },
    { label: "Farm", value: ExportGrouping.Farm },
    { label: "Grower", value: ExportGrouping.Grower },
    { label: "All Growers", value: ExportGrouping.AllGrowers },
];

interface IProps {
    recSummaries: RecSummary[];
    isGenericType: boolean;
    groupingMethod: ExportGrouping | null;
    multiProduct: boolean;
    exportFormat: string | null;
    showInputs: boolean;
    updateState: (newValue: IExportControllerFilenameOutput, filenamesValid: boolean) => void;
}

const invalidCharacters = new RegExp(/[!@#$%^&*[\]{};:,\\/<>?|`~=+'"().]/g);

function isValid(filename: string | null): boolean {
    return (filename ?? "" !== "") && filename.replace(invalidCharacters, "") === filename;
}

function extractProductsEquationApplication(detail: RecAPI.IRecDetails): IRecProduct[] {
    return detail.recAreaList
        .filter((area) => area.applyRecToArea)
        .flatMap((area) => area.recs)
        .flatMap((rec) =>
            rec.recNutrientList.map((nutrient) => ({
                recGeneralGuid: detail.recGeneralGuid,
                nutrient: nutrient.equationName.replace(" Rec", "").trim(),
                productMixName: nutrient.recNutrientProductMix.name.trim(),
            }))
        );
}

function extractProductsManualApplication(detail: RecAPI.IRecDetails): IRecProduct[] {
    const uniqueMixes = uniqBy(
        (detail as any).zones
            .filter((zone) => zone.applyToArea)
            .flatMap((zone) => zone.details)
            .flatMap((detail) => detail.productMixList as ProductMix[]),
        (x: ProductMix) => x.name
    );

    return uniqueMixes.map((mix: ProductMix, index) => ({
        recGeneralGuid: detail.recGeneralGuid,
        nutrient: `Mix ${index + 1}`,
        productMixName: uniqueMixes.length > 1 ? `${mix.name} - Mix ${index + 1}` : mix.name,
    }));
}

function renderFilenameInput(
    recIndex: number,
    nutrientIndex: number,
    nutrient: INutrientFilename,
    erroredControls: Set<string>,
    formatMessage: (message: MessageDescriptor) => string,
    updateFilename: (recIndex: number, nutrientIndex: number, filename: string | null) => void
) {
    return (
        <React.Fragment key={`${recIndex}-${nutrientIndex}`}>
            {nutrient.nutrient && <label>{nutrient.nutrient}</label>}
            <TextInput
                placeholderText="Rec File Name"
                required
                hasError={erroredControls.has(`file-${recIndex}-${nutrientIndex}`)}
                errorText={formatMessage(
                    nutrient.filename
                        ? messages.exportValidationWarningFilenameSpecialCharacters
                        : messages.exportValidationWarningFilenameRequired
                )}
                value={nutrient.filename}
                onChange={(filename) => updateFilename(recIndex, nutrientIndex, filename)}
                onBlur={() => updateFilename(recIndex, nutrientIndex, nutrient.filename.trim())}
                testId={`export-custom-filename-rec-file-name-input-${recIndex}-${nutrientIndex}`}
            />
        </React.Fragment>
    );
}

const MULTIPLE_LABEL = "Multiple";

export function ExportControllerCustomFilename(props: IProps) {
    const { isGenericType, showInputs, recSummaries, groupingMethod, multiProduct, exportFormat } =
        props;
    const userGuid = useAppSelector(getTheUserGuid);

    const { formatMessage } = useIntl();
    const [erroredControls, setErroredControls] = useState(new Set<string>());
    const [zipFilename, setZipFilename] = useState<string>(null);
    const [recProducts, setRecProducts] = useState<IRecProduct[][]>([]);
    const [recFilenames, setRecFilenames] = useState<IRecFilenames[]>([]);
    const [recNameStart, setRecNameStart] = useState<string>(null);
    const [recNameEnd, setRecNameEnd] = useState<string>(null);
    const [showLoader, setShowLoader] = useState(true);

    const templatedFormat = exportFormat ? `{${exportFormat.replaceAll("/", "}_{")}}` : null;

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (
                recSummaries.some(
                    (x) =>
                        x.recType === REC_TYPE_NAME_EQUATION_APPLICATION ||
                        x.recType === REC_TYPE_NAME_MANUAL_APPLICATION
                )
            ) {
                const recPromises = recSummaries.map((x) =>
                    RecAPI.getRec(userGuid, x.recGeneralGuid)
                );
                Promise.all(recPromises).then((details) => {
                    if (!isMounted) return;

                    const recProducts: IRecProduct[][] = details.map((detail) => {
                        if (detail.recType === REC_TYPE_NAME_EQUATION_APPLICATION) {
                            return extractProductsEquationApplication(detail);
                        } else if (detail.recType === REC_TYPE_NAME_MANUAL_APPLICATION) {
                            return extractProductsManualApplication(detail);
                        }

                        return [];
                    });
                    setRecProducts(recProducts);
                    setShowLoader(false);
                });
            }
        };
        fetchData();
        return () => {
            isMounted = false;
        };
    }, [recSummaries, userGuid]);
    useEffect(() => {
        setTimeout(() => rebuildRecFiles(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recProducts, multiProduct, templatedFormat, showInputs]);
    useEffect(() => {
        setTimeout(() => rebuildRecFiles(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recNameStart, recNameEnd, groupingMethod]);
    useEffect(() => {
        props.updateState(
            {
                zipFilename,
                exportFilenames: recFilenames?.flatMap((rec) =>
                    rec.files.map((product) => ({
                        fieldName: rec.fieldName,
                        farmName: rec.farmName,
                        customerName: rec.customerName,
                        customerGuid: rec.customerGuid,
                        recGeneralGuid: product.recGeneralGuid,
                        productMixName:
                            product.nutrient?.startsWith("Mix ") ?? false
                                ? product.productMixName.replace(` - ${product.nutrient}`, "")
                                : product.productMixName,
                        filename: product.filename,
                    }))
                ),
            },
            erroredControls.size === 0
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zipFilename, recFilenames, erroredControls]);

    if (!showInputs) {
        return null;
    }

    function rebuildRecFiles(updateZipFile: boolean) {
        let newZipFilename: string;
        let recFilenames: IRecFilenames[];
        if (recSummaries?.length < 1) {
            return;
        }
        const customerName = recSummaries.every(
            (x) => x.customerName === recSummaries[0].customerName
        )
            ? recSummaries[0].customerName.trim()
            : MULTIPLE_LABEL;
        const farmName = recSummaries.every((x) => x.farmName === recSummaries[0].farmName)
            ? recSummaries[0].farmName.trim()
            : MULTIPLE_LABEL;
        const fieldName = recSummaries.every((x) => x.fieldName === recSummaries[0].fieldName)
            ? recSummaries[0].fieldName.trim()
            : MULTIPLE_LABEL;
        const firstProductMixName =
            recProducts.find((x) => x.length > 0)?.[0].productMixName ?? null;
        const productName = recProducts
            .flatMap((x) => x)
            .every((x) => x.productMixName === firstProductMixName)
            ? firstProductMixName?.trim()
            : MULTIPLE_LABEL;

        if (multiProduct) {
            newZipFilename = buildFilenameFromFormat(
                customerName,
                farmName,
                fieldName,
                productName,
                "MultiProduct_",
                true
            );

            const byField = Object.values(groupBy(recSummaries, (x) => x.fieldGuid));
            recFilenames = byField.map((summaries) => {
                const customerName = summaries.every(
                    (x) => x.customerName === summaries[0].customerName
                )
                    ? summaries[0].customerName.trim()
                    : MULTIPLE_LABEL;
                const farmName = summaries.every((x) => x.farmName === summaries[0].farmName)
                    ? summaries[0].farmName.trim()
                    : MULTIPLE_LABEL;
                const fieldName = summaries.every((x) => x.fieldName === summaries[0].fieldName)
                    ? summaries[0].fieldName.trim()
                    : MULTIPLE_LABEL;
                const productNames = uniq(
                    recProducts.flatMap((x) => x).flatMap((x) => x.productMixName)
                );
                const recGuid = summaries.length === 1 ? summaries[0].recGeneralGuid : null;

                return {
                    customerGuid:
                        customerName !== MULTIPLE_LABEL ? summaries[0].customerGuid : null,
                    customerName,
                    farmName,
                    fieldName,
                    files: [
                        {
                            recGeneralGuid: recGuid,
                            nutrient: null,
                            productMixName: null,
                            filename: buildFilenameFromFormat(
                                customerName,
                                farmName,
                                fieldName,
                                productNames.length === 1 ? productNames[0] : MULTIPLE_LABEL
                            ),
                        },
                    ],
                };
            });
        } else {
            newZipFilename = buildFilenameFromFormat(
                customerName,
                farmName,
                fieldName,
                productName,
                "",
                true
            );
            recFilenames = recProducts.map((products, recIndex) => {
                const customerName = recSummaries[recIndex].customerName?.trim() ?? "";
                const farmName = recSummaries[recIndex].farmName.trim();
                const fieldName = recSummaries[recIndex].fieldName.trim();
                const files = products.map((product) => ({
                    ...product,
                    filename: buildFilenameFromFormat(
                        customerName,
                        farmName,
                        fieldName,
                        product.productMixName
                    ),
                }));

                return {
                    customerName,
                    farmName,
                    fieldName,
                    customerGuid: recSummaries[recIndex].customerGuid,
                    fieldGuid: recSummaries[recIndex].fieldGuid,
                    recGeneralGuid: recSummaries[recIndex].recGeneralGuid,
                    files: uniqBy(files, "productMixName"),
                };
            });
        }

        const groupedRecFilenames = groupRecFilenamesBy(recFilenames);
        const errors = new Set<string>();

        groupedRecFilenames.forEach((rec, recIndex) => {
            rec.files.forEach((nutrient, nutrientIndex) => {
                if (!isValid(nutrient.filename)) {
                    errors.add(`file-${recIndex}-${nutrientIndex}`);
                }
            });
        });

        setRecFilenames(groupedRecFilenames);
        if (updateZipFile) {
            if (!isValid(newZipFilename)) {
                errors.add("zipFilename");
            }
            setZipFilename(newZipFilename);
        } else if (!isValid(zipFilename)) {
            errors.add("zipFilename");
        }
        if (recNameStart && !isValid(recNameStart)) {
            errors.add("recNameStart");
        }
        if (recNameEnd && !isValid(recNameEnd)) {
            errors.add("recNameEnd");
        }
        setErroredControls(errors);
    }

    function updateZipName(newName: string | null) {
        if (isValid(newName)) {
            erroredControls.delete("zipFilename");
        } else {
            erroredControls.add("zipFilename");
        }
        setZipFilename(newName);
        setErroredControls(erroredControls);
    }

    function updateFilename(recIndex: number, nutrientIndex: number, filename: string | null) {
        recFilenames[recIndex].files[nutrientIndex].filename = filename;
        if (isValid(filename)) {
            erroredControls.delete(`file-${recIndex}-${nutrientIndex}`);
        } else {
            erroredControls.add(`file-${recIndex}-${nutrientIndex}`);
        }
        setRecFilenames([...recFilenames]);
        setErroredControls(erroredControls);
    }

    function buildFilenameFromFormat(
        grower: string,
        farm: string,
        field: string,
        product: string,
        extraPrefix = "",
        isZipFile = false
    ): string {
        // this comes from the export controller in ai360.python
        return (
            (isZipFile ? "" : recNameStart ?? "") +
            extraPrefix +
            (templatedFormat
                ?.replace("{Grower}", grower.replaceAll(" ", ""))
                .replace("{Farm}", farm?.replaceAll(" ", "") ?? "")
                .replace("{Field}", field?.replaceAll(" ", ""))
                .replace("{Product}", product?.replaceAll(" ", "") ?? "")
                .replaceAll("/", "_")
                .replaceAll(" - Mix", " Mix")
                .trim()
                .replaceAll(invalidCharacters, "")
                .replaceAll(" ", isZipFile ? "" : "_")
                .replaceAll(/_{2,}/g, "_")
                .replaceAll(/^_|_$/gm, "") ?? "") +
            (isZipFile ? "" : recNameEnd ?? "")
        );
    }

    const showPrePostfix = reduce(recFilenames, (prev, curr) => prev + curr.files.length, 0) > 1;

    const sectionClass =
        (!recFilenames || recFilenames[0]?.files[0]?.nutrient) ?? false
            ? "file-list"
            : "file-list one-column";

    function groupRecFilenamesBy(recs: IRecFilenames[]): IRecFilenames[] {
        if (groupingMethod === ExportGrouping.Field) {
            return recs;
        }

        const groupedRecs = reduce(
            recs,
            (accum, rec) => {
                let key = rec.customerName;
                if (groupingMethod == ExportGrouping.Farm) {
                    key += `~${rec.farmName}`;
                } else if (groupingMethod === ExportGrouping.AllGrowers) {
                    key = "";
                }
                const existingValue = accum.get(key);
                if (existingValue) {
                    if (existingValue.customerGuid !== rec.customerGuid) {
                        existingValue.customerGuid = "";
                        existingValue.customerName = MULTIPLE_LABEL;
                    }
                    if (existingValue.fieldName !== rec.fieldName) {
                        existingValue.fieldName = MULTIPLE_LABEL;
                    }
                    if (existingValue.farmName !== rec.farmName) {
                        existingValue.farmName = MULTIPLE_LABEL;
                    }
                    existingValue.files = existingValue.files.concat(rec.files);
                    accum.set(key, existingValue);
                } else {
                    accum.set(key, rec);
                }
                return accum;
            },
            new Map<string, IRecFilenames>()
        ).values();

        return Array.from(
            groupedRecs.map((rec) => ({
                ...rec,
                files: uniqBy(rec.files, "productMixName").map((product) => ({
                    ...product,
                    filename: buildFilenameFromFormat(
                        rec.customerName,
                        rec.farmName,
                        rec.fieldName,
                        product.productMixName ?? MULTIPLE_LABEL
                    ),
                })),
            }))
        );
    }

    function renderRec(rec: IRecFilenames, recIndex: number) {
        let sectionTitle: string = null;
        if (recFilenames.length > 1) {
            switch (groupingMethod) {
                case ExportGrouping.Field:
                    sectionTitle = `${rec.customerName} ${
                        rec.farmName ? `> ${rec.farmName} ` : ""
                    } > ${rec.fieldName.trim()}`;
                    break;
                case ExportGrouping.Farm:
                    sectionTitle = `${rec.customerName} ${
                        rec.farmName ? `> ${rec.farmName} ` : ""
                    }`;
                    break;
                case ExportGrouping.Grower:
                    sectionTitle = rec.customerName;
                    break;
            }
        }

        return (
            rec.files.length > 0 && (
                <React.Fragment key={recIndex}>
                    {sectionTitle && <div className="field">{sectionTitle}</div>}
                    {recFilenames[recIndex].files.map((nutrient, nutrientIndex) =>
                        renderFilenameInput(
                            recIndex,
                            nutrientIndex,
                            nutrient,
                            erroredControls,
                            formatMessage,
                            updateFilename
                        )
                    )}
                </React.Fragment>
            )
        );
    }

    return (
        <div className="export-controller-custom-filename">
            <div className="zip-name">
                <TextInput
                    placeholderText="Zip File Name"
                    value={zipFilename}
                    hasError={erroredControls.has("zipFilename")}
                    required={true}
                    errorText={formatMessage(
                        zipFilename
                            ? messages.exportValidationWarningFilenameSpecialCharacters
                            : messages.exportValidationWarningFilenameRequired
                    )}
                    onChange={(newName) => updateZipName(newName)}
                    onBlur={() => updateZipName(zipFilename?.trim())}
                    testId="export-custom-filename-zip-file-name-input"
                />
            </div>
            {isGenericType && (
                <React.Fragment>
                    {showLoader ? (
                        <Loader type={LoaderTypes.LINE_SCALE_PULSE_OUT} />
                    ) : (
                        <React.Fragment>
                            {showPrePostfix && (
                                <React.Fragment>
                                    <TextInput
                                        placeholderText="Start All Rec File Names With"
                                        value={recNameStart}
                                        hasError={erroredControls.has("recNameStart")}
                                        errorText={formatMessage(
                                            messages.exportValidationWarningFilenameSpecialCharacters
                                        )}
                                        onChange={(value) => setRecNameStart(value)}
                                        testId="export-custom-filename-rec-name-start-input"
                                    />
                                    <TextInput
                                        placeholderText="End All Rec File Names With"
                                        value={recNameEnd}
                                        hasError={erroredControls.has("recNameEnd")}
                                        errorText={formatMessage(
                                            messages.exportValidationWarningFilenameSpecialCharacters
                                        )}
                                        onChange={(value) => setRecNameEnd(value)}
                                        testId="export-custom-filename-rec-name-end-input"
                                    />
                                </React.Fragment>
                            )}
                            <div className={sectionClass}>
                                {recFilenames?.map((rec, recIndex) => renderRec(rec, recIndex))}
                            </div>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </div>
    );
}
